import React from 'react';

import { faFile } from '@fortawesome/pro-light-svg-icons';
import { faCircleXmark } from '@fortawesome/pro-solid-svg-icons';
import styles from './History.module.scss';
import { Export } from './ExportToFile';
import {
  Badge,
  Button,
  Heading,
  Icon,
  IconList,
  Text,
  Loading,
  StructuredList,
  StructuredListItem,
  StructuredListSecondaryActions,
  Card,
} from 'components/core';
import { Inline, Stack } from 'components/layout';

export type HistoryProps = {
  exports: Export[];
};

export function formatDisplayLabel(format: string): string {
  const map: Record<string, string> = {
    section_excel: 'per_section_xlsx',
    csv: 'single_sheet_csv',
    xlsx: 'per_study_xlsx',
  };

  return map[format] ?? format;
}

const ExportTable = ({ exports }: HistoryProps) => {
  if (exports.length == 0) {
    return (
      <Text className={styles.exportSubheading}>
        There have been no exports in the last 7 days
      </Text>
    );
  }

  return (
    <>
      <Text className={styles.exportSubheading}>In the last 7 days</Text>
      <StructuredList className={styles.exportHistory}>
        {exports.map((ex: Export) => (
          <ExportRow key={ex.id} {...ex} />
        ))}
      </StructuredList>
    </>
  );
};

const ExportIcon = ({ completedAt, failed }: Export) => {
  if (completedAt) {
    return <Icon icon={faFile} size="lg" />;
  }

  if (failed) {
    return <Icon icon={faCircleXmark} variant="danger" size="lg" />;
  }

  return <Loading type="primary" size="md" />;
};

const exportedTime = ({ completedAt, failed }: Export): string => {
  if (completedAt) {
    return `Exported ${completedAt}`;
  }

  if (failed) {
    return 'Export failed';
  }

  return 'Exporting...';
};

const ExportDescription = (ex: Export) => {
  return (
    <Inline spacing={3} alignX="spaceBetween">
      <Stack spacing={0}>
        <Inline spacing={2} alignX="spaceBetween">
          <Text
            size="sm"
            variant="neutral"
            className={styles.exportDescription}
          >
            {ex.type} - {ex.description}
          </Text>
          {ex.failed && (
            <Badge type="danger" variant="solid">
              Failed
            </Badge>
          )}
        </Inline>
        <Text size="xs" variant="neutral">
          {formatDisplayLabel(ex.format)} ·
          <span className={styles.exportedTime}> {exportedTime(ex)}</span>
        </Text>
      </Stack>
    </Inline>
  );
};

const ExportRow = (ex: Export) => {
  return (
    <StructuredListItem icon={<ExportIcon {...ex} />}>
      <ExportDescription {...ex} />
      {ex.completedAt && (
        <StructuredListSecondaryActions>
          <Button
            variant="outline"
            size="sm"
            type="brand"
            to={ex.authenticatedUrl}
          >
            Download
            <Icon icon={IconList.light.faArrowDownToLine} />
          </Button>
        </StructuredListSecondaryActions>
      )}
    </StructuredListItem>
  );
};

const History = ({ exports }: HistoryProps) => {
  return (
    <Card id="export-history">
      <Heading type="h5" className={styles.exportHeading}>
        Recent exports
      </Heading>
      <ExportTable exports={exports} />
    </Card>
  );
};

export default History;
